import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Text,
} from '@chakra-ui/react';
import { useContext } from 'react';
import { useMediaQuery } from 'react-responsive';
import { GlobalContext } from 'providers/Global';
import { ModalType, SCREEN_MD } from '../../utils/constants';

const faqItems = [
  {
    question: 'What is a multisig account?',
    answer:
      'A multisig (multi-signature) account is a Hedera account controlled by multiple key holders rather than a single private key. Transactions from this account require approval from a defined number of signers before they can be executed. This provides shared custody, reducing the risk of a single point of failure and enabling team-based asset management.',
  },
  {
    question: 'How do thresholds work?',
    answer:
      'A threshold defines the minimum number of signers required to approve a transaction. For example, in a 2-of-3 setup, at least 2 out of 3 designated signers must approve before a transaction is executed. You set the threshold during account creation, and it can be updated later through a threshold update transaction (which itself requires the current threshold of approvals).',
  },
  {
    question: 'What is the maximum duration of a scheduled transaction on Hedera?',
    answer:
      'Hedera scheduled transactions have a maximum duration of approximately 5 million seconds (around 62 days). All required signers must submit their approvals within that window. If the threshold is not met before the transaction expires, it is discarded and must be recreated. While 62 days provides a generous timeframe, it is still important to coordinate with your co-signers to ensure timely approval.',
  },
  {
    question: 'What types of transactions does Hedera MultiSig support?',
    answer:
      'The application supports several transaction types: HBAR transfers (sending native HBAR between accounts), HTS token transfers (sending Hedera Token Service tokens), contract interactions (calling smart contract functions), token associations (linking an HTS token to your account), threshold updates (changing the number of required signers), and fund transactions (depositing HBAR into the multisig account).',
  },
  {
    question: 'What is token association and why is it required?',
    answer:
      'On Hedera, accounts must explicitly associate with a token before they can receive it. This is a Hedera Token Service (HTS) design choice that differs from Ethereum\'s ERC-20 model, where any token can be sent to any address. Token association protects accounts from receiving unwanted or spam tokens. Before your multisig account can hold or receive an HTS token, you need to create a token association transaction approved by the required signers.',
  },
  {
    question: 'Which wallets are supported?',
    answer:
      'Hedera MultiSig supports HashPack (as a browser extension for direct connection) and any WalletConnect-compatible wallet. WalletConnect v2 protocol is used, so wallets like Blade and others that support this standard will work as well.',
  },
  {
    question: 'What networks does Hedera MultiSig support?',
    answer:
      'The application supports both Testnet and Mainnet. You can select your preferred network during wallet connection. Testnet is recommended for experimenting and testing before managing real assets on Mainnet.',
  },
  {
    question: 'Is my multisig account stored on-chain?',
    answer:
      'Yes — the multisig account itself is a native Hedera account with a threshold key structure, fully on-chain. The application additionally stores metadata such as signer labels, descriptions, and transaction history in a backend database for convenience and a better user experience. The on-chain account and its key structure are the source of truth for security.',
  },
  {
    question: 'How do I add or remove signers from an existing multisig?',
    answer:
      'You can modify the signer set through a threshold update transaction. This transaction proposes a new key list and threshold for the account. Like any other multisig transaction, it requires approval from the current threshold of signers before it takes effect. Navigate to the Settings page of your multisig account to initiate this process.',
  },
  {
    question: 'Can I use the same wallet across multiple multisig accounts?',
    answer:
      'Yes. A single Hedera account can be a signer on multiple multisig accounts. When you connect your wallet, the application will show all multisig accounts where your account ID is listed as a signer, allowing you to switch between them from the homepage.',
  },
];

const FAQDrawer = () => {
  const {
    modals: {
      [`${ModalType.FAQDrawer}`]: { open },
    },
    closeModal,
  } = useContext(GlobalContext);

  const isMobile = useMediaQuery({ query: `(max-width: ${SCREEN_MD})` });

  return (
    <Drawer
      isOpen={open}
      onClose={() => closeModal(ModalType.FAQDrawer)}
      placement="right"
      size={isMobile ? 'full' : 'lg'}
    >
      <DrawerOverlay backdropFilter="blur(10px)" />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader borderBottomWidth="1px">
          <Text variant="bodyBold" fontSize="xl">
            Frequently Asked Questions
          </Text>
        </DrawerHeader>
        <DrawerBody py={6}>
          <Accordion allowMultiple>
            {faqItems.map((item, index) => (
              <AccordionItem key={index} border="none" mb={2}>
                <AccordionButton
                  py={4}
                  px={3}
                  borderRadius={8}
                  _hover={{ bg: 'blackAlpha.50' }}
                  _expanded={{ bg: 'blackAlpha.50' }}
                >
                  <Box flex={1} textAlign="left">
                    <Text fontWeight={600} fontSize="sm">
                      {item.question}
                    </Text>
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel px={3} pb={4}>
                  <Text fontSize="sm" color="blackAlpha.700" lineHeight="tall">
                    {item.answer}
                  </Text>
                </AccordionPanel>
              </AccordionItem>
            ))}
          </Accordion>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

export default FAQDrawer;
